// /src/components/Modals/AddWorkspaceModal.tsx

// What? A modal for adding a new workspace to the application.
// Why? To allow users to add new workspaces to the application.
// How?
// - Uses a Modal component to display the modal.
// - Uses a ModalDialog component to display the modal content.
// - Uses a FormControl component to display the form.
// - Uses a FormLabel component to display the label for the workspace name input.
// - Uses an Input component to display the workspace name input.

import React, { useEffect, useState, useMemo, useCallback } from "react";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import DialogTitle from "@mui/joy/DialogTitle";
import Stack from "@mui/joy/Stack";
import Input from "@mui/joy/Input";
import { Button, Box, Typography, CircularProgress, RadioGroup, Radio } from "@mui/joy";
import { moduleRegistry } from "../../modules/moduleRegistry";
import { useAvailableModules } from "../../services/api";

// How to instantiate:
// <AddWorkspaceModal
//   open={open}
//   handleAddWorkspace={handleAddWorkspace}
//   onClose={onClose}
//   buttonDisabled={buttonDisabled}
//   buttonLabel={buttonLabel}
// />
interface AddWorkspaceModalProps {
  open: boolean;
  handleAddWorkspace: (
    workspaceName: string,
    selectedModules: string[],
  ) => void;
  onClose: () => void;
  buttonDisabled: boolean; // New prop to disable the button
  buttonLabel: React.ReactNode; // New prop to display label or spinner
}

// What? A modal for adding a new workspace to the application.
// Why? To allow users to add new workspaces to the application.
// How?
// This component implements a modal for adding a new workspace by:
// 1. Rendering a modal dialog when the 'open' prop is true
// 2. Displaying a form within the modal with a field for workspace name
// 3. Managing form state (workspace name) using React hooks
// 4. Handling form submission and passing data to the parent component
// 5. Providing options to submit the form or cancel the operation
// 6. Resetting the form state when the modal is closed
//
// The component uses various MUI Joy components to create a consistent UI,
// including Modal, ModalDialog, FormControl, Input, and Button.
const AddWorkspaceModal: React.FC<AddWorkspaceModalProps> = ({
  open,
  handleAddWorkspace,
  onClose,
  buttonDisabled,
  buttonLabel,
}) => {
  const [addWorkspaceOpen, setAddWorkspaceOpen] = React.useState<boolean>(open);
  const [selectedModules, setSelectedModules] = useState<string[]>([]); // No default modules
  const [availableModuleIds, setAvailableModuleIds] = useState<string[]>([]);
  const [loadingModules, setLoadingModules] = useState<boolean>(true);

  const getAvailableModules = useAvailableModules();

  // Filter module registry based on what's actually available on the backend
  const availableModules = useMemo(() => {
    return Object.entries(moduleRegistry).filter(([moduleId]) =>
      availableModuleIds.includes(moduleId),
    );
  }, [availableModuleIds]);

  useEffect(() => {
    setAddWorkspaceOpen(open);
  }, [open]);

  // Fetch available modules when modal opens
  useEffect(() => {
    if (open) {
      setLoadingModules(true);
      getAvailableModules()
        .then((response: { enabled: string[]; config: any }) => {
          setAvailableModuleIds(response.enabled);
        })
        .catch((error: any) => {
          console.error("Failed to fetch available modules:", error);
          // Don't show any modules if API fails - this ensures we only show what's actually available
          setAvailableModuleIds([]);
        })
        .finally(() => {
          setLoadingModules(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]); // Removed getAvailableModules from dependencies to prevent infinite loop

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Ensure at least one module is selected and available
    if (selectedModules.length === 0) {
      if (availableModules.length === 0) {
        alert(
          "No modules are currently available. Please contact your administrator.",
        );
      } else {
        alert("Please select at least one module for your workspace.");
      }
      return;
    }

    const formData = new FormData(event.currentTarget);
    const workspaceName = formData.get("workspaceName") as string;
    handleAddWorkspace(workspaceName, selectedModules);
  };

  const handleModuleSelect = useCallback((moduleId: string) => {
    setSelectedModules([moduleId]);
  }, []);

  return (
    <Modal open={addWorkspaceOpen} onClose={onClose}>
      <ModalDialog className="modal">
        <DialogTitle>Add new workspace</DialogTitle>
        <form onSubmit={handleFormSubmit}>
          <Stack spacing={2}>
            <FormControl>
              <FormLabel>Workspace Name</FormLabel>
              <Input
                name="workspaceName"
                className="inputField"
                autoFocus
                required
                placeholder="Workspace Name"
              />
            </FormControl>

            <Box>
              <Typography level="body-sm" fontWeight="md" sx={{ mb: 0.5 }}>
                Select Module
              </Typography>
              <Typography
                level="body-xs"
                sx={{ color: "text.secondary", mb: 1 }}
              >
                Select your module, one per workspace.
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  minHeight: "60px",
                }}
              >
                {loadingModules ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      py: 1,
                    }}
                  >
                    <CircularProgress size="sm" />
                    <Typography
                      level="body-sm"
                      sx={{ color: "text.secondary" }}
                    >
                      Loading available modules...
                    </Typography>
                  </Box>
                ) : availableModules.length === 0 ? (
                  <Typography
                    level="body-sm"
                    sx={{ color: "warning.main", py: 1 }}
                  >
                    No modules are currently available. Please contact your
                    administrator.
                  </Typography>
                ) : (
                  <RadioGroup
                    value={selectedModules[0] || ""}
                    onChange={(e) => handleModuleSelect(e.target.value)}
                  >
                    {availableModules.map(([moduleId, moduleConfig]) => (
                      <Radio
                        key={moduleId}
                        value={moduleId}
                        label={
                          <Box sx={{ display: "flex", flexDirection: "column" }}>
                            <Typography level="body-sm" fontWeight="md">
                              {moduleConfig.name}
                            </Typography>
                            {moduleConfig.description && (
                              <Typography level="body-xs" sx={{ color: "text.secondary" }}>
                                {moduleConfig.description}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    ))}
                  </RadioGroup>
                )}
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button
                variant="solid"
                color="neutral"
                onClick={onClose}
                disabled={buttonDisabled}
              >
                Cancel
              </Button>
              {/* Disable the create button while the workspace is being created and show the spinner */}
              <Button
                variant="solid"
                color="primary"
                type="submit"
                disabled={
                  buttonDisabled ||
                  selectedModules.length === 0 ||
                  loadingModules ||
                  availableModules.length === 0
                }
              >
                {buttonLabel}
              </Button>
            </Box>
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
};

export default AddWorkspaceModal;
